import { CandlestickData } from "lightweight-charts";
import * as ta from "./core/indicators"
import { ChartCandle } from "../../components/chartV2/chart.typings";
import { getCurrentOpenInterests } from "../../services/apis/optionChain";
import { Security } from "../../proto/generated/security";
import { TimeBucket, TimeBucket_TimeSeries } from "../../proto/generated/common";
import { CalendarData } from '../chart'

const IST_OFFSET_MILLIS = 19800000;

export class IndicatorUtils {
    candles: CandlestickData[]
    referenceTime: number
    prevCandles: CandlestickData[]
    candleFetcher: Function
    security?: Security

    constructor(security?: Security, candleFetcher?: Function, candles?: ChartCandle[], prevCandles?: CandlestickData[]) {
        this.security = security
        this.candles = (candles ?? []) as CandlestickData[]
        this.referenceTime = (candles && candles.length > 0) ? Number(candles[0].time) * 1000 : (new Date().getTime())
        this.prevCandles = prevCandles ? prevCandles : []
        this.candleFetcher = candleFetcher ?? (() => {
        })
    }

    rsi(source: number[], period: number, size = source.length) {
        return ta.rsi(source, period, size)
    }

    add(series1: number[], series2: number[]) {
        const ans = [];
        const lengthDiff = series1.length - series2.length
        if (lengthDiff >= 0) {
            for (let i = 0; i < series2.length; i++) {
                ans.push(series1[i + lengthDiff] + series2[i]);
            }
        } else {
            for (let i = 0; i < series1.length; i++) {
                ans.push(series1[i] + series2[i - lengthDiff]);
            }
        }
        return ans;
    }

    getTimeframesFromStartEnd(start: number, end: number, step: number): [number, number][] {
        const timeframes: [number, number][] = [];
        for (let i = start; i < end; i += step) {
            timeframes.push([i, i + step])
        }
        return timeframes
    }


    getTimeframePeriod(timeframeDuration: string): number {
        switch (timeframeDuration) {
            case '1d': // Day
                return 24 * 60 * 60 * 1000;
            case '1w': // Week
                return 7 * 24 * 60 * 60 * 1000;
            case '1m': // Month (approximated to 30 days)
                return 30 * 24 * 60 * 60 * 1000;
            case '3m': // 3 Months (approximated to 90 days)
                return 90 * 24 * 60 * 60 * 1000;
            case '6m': // 6 Months (approximated to 180 days)
                return 180 * 24 * 60 * 60 * 1000;
            case '1y': // Year (approximated to 365 days)
                return 365 * 24 * 60 * 60 * 1000;
            default:
                throw new Error('Invalid timeframe duration');
        }
    }

    getPrevValues(series: number[], length: number): number[] {
        return series.map((s, ind) => (ind - length >= 0 ? series[ind - length] : s))
    }

    getTimeBucket(timeframe: string): TimeBucket {
        let timeBucket: TimeBucket;
        switch (timeframe) {
            case '1d':
                timeBucket = { timeSeries: TimeBucket_TimeSeries.DAY, value: 1 }; // 5 minutes in milliseconds
                break;
            case '1w':
                timeBucket = { timeSeries: TimeBucket_TimeSeries.DAY, value: 7 }; // 5 minutes in milliseconds
                break;
            case '1m':
                timeBucket = { timeSeries: TimeBucket_TimeSeries.MONTH, value: 1 };
                break;
            case '3m':
                timeBucket = { timeSeries: TimeBucket_TimeSeries.MONTH, value: 3 };
                break;
            case '6m':
                timeBucket = { timeSeries: TimeBucket_TimeSeries.MONTH, value: 6 };
                break;
            case '1y':
                timeBucket = { timeSeries: TimeBucket_TimeSeries.MONTH, value: 12 };
                break;
            default:
                // Handle unsupported timeframes
                throw new Error('Unsupported timeframe');
        }
        return timeBucket

    }

    getPivotValue(h: number, l: number, c: number, pivotType: string, label: string) {
        const P = (h + l + c) / 3
        const R1 = 2 * P - l
        const S1 = 2 * P - h

        switch (label) {
            case ('P'): {
                return P;
                break;
            }
            case ('R1'): {
                return R1;
                break;
            }
            case ('S1'): {
                return S1;
                break;
            }
            case ('R2'): {
                return P + (h - l);
                break;
            }
            case ('S2'): {
                return P - (h - l);
                break;
            }
            case ('R3'): {
                return P + 2 * (h - l);
                break;
            }
            case ('S3'): {
                return P - 2 * (h - l);
                break;
            }
        }

    }

    async getPivotPointshlc(timeStamps: number[], timeFrame: string): Promise<any> {
        if (timeStamps.length === 0) {
            return []
        }
        const timeBucket = this.getTimeBucket(timeFrame)
        const timePeriod = this.getTimeframePeriod(timeFrame)
        const requiredCandles = Math.ceil((timeStamps[timeStamps.length - 1] - timeStamps[0]) / timePeriod + 1)
        const referenceTime = timeStamps[timeStamps.length - 1]
        const candles = await this.candleFetcher(requiredCandles, referenceTime, timeBucket) as CandlestickData[]
        const classicLevels = ['P', 'R1', 'S1', 'R2', 'S2', 'R3', 'S3']

        const ans: any[] = [{ label: 'P', dataPoints: [], }, { label: 'R1', dataPoints: [] }, {
            label: 'S1',
            dataPoints: []
        }, { label: 'R2', dataPoints: [] }, { label: 'S2', dataPoints: [] }, { label: 'R3', dataPoints: [] }, {
            label: 'S3',
            dataPoints: []
        }]
        var startTime = 0;
        var endTime = 0;
        for (let i = 0; i < candles.length; i++) {
            if (i === candles.length - 1) {
                startTime = (candles[i].time as number) * 1000
                endTime = timeStamps[timeStamps.length - 1]
            } else {
                startTime = (candles[i].time as number) * 1000
                endTime = (candles[i + 1].time as number) * 1000
            }
            const h = candles[i].high
            const l = candles[i].low
            const c = candles[i].close
            if (h !== 0 || l !== 0 || c !== 0) {
                ans.forEach((levelData) => {
                    if (!classicLevels.includes(levelData.label)) {
                        return
                    }
                    levelData.dataPoints.push({
                        startTime: startTime,
                        endTime: endTime,
                        val: this.getPivotValue(h, l, c, 'Classic', levelData.label)
                    })
                })

            }
        }
        return ans;
    }


    svKlinger(series: number[], volume: number[]): number[] {
        const ans: number[] = [];
        series.forEach((element, index) => {
            if (element >= 0) {
                ans.push(volume[index]);
            } else {
                ans.push(-volume[index]);
            }
        });
        return ans;
    }

    getASI(high: number[], low: number[], close: number[], open: number[], limit: number): number[] {
        const H_C1 = this.abs(this.subtract(high, this.shiftSeries(close, 1, 0)))
        const L_C1 = this.abs(this.subtract(low, this.shiftSeries(close, 1, 0)))
        const H_L = this.abs(this.subtract(high, low))
        const C1_O1 = this.abs(this.subtract(this.shiftSeries(close, 1, 0), this.shiftSeries(open, 1, 0)))
        const K = this.compareMax(H_C1, L_C1)
        const R = H_C1.map((val, i) => {
            if (val >= Math.max(L_C1[i], H_L[i])) {
                return val - 0.5 * L_C1[i] + 0.25 * C1_O1[i];
            } else if (L_C1[i] >= Math.max(H_C1[i], H_L[i])) {
                return L_C1[i] - 0.5 * H_C1[i] + 0.25 * C1_O1[i];
            } else {
                return H_L[i] + 0.25 * C1_O1[i];
            }
        });
        const result = this.multiplyConstant(this.multiply(this.divide(this.add(this.subtract(close, this.shiftSeries(close, 1, 0)), this.add(this.multiplyConstant(this.subtract(close, open), 0.5), this.multiplyConstant(this.subtract(this.shiftSeries(close, 1, 0), this.shiftSeries(open, 1, 0)), 0.25))), R), K), 50 / limit)
        return this.cum(result)
    }

    _round(val: number): number {
        if (val > 0.99) return 0.999;
        else if (val < -0.99) return -0.999;
        else return val;
    };

    fisher(high: number[], low: number[], length: number): number[] {
        const hl2 = this.multiplyConstant(this.add(high, low), 0.5)
        const high_ = this.highest(hl2, length)
        const low_ = this.lowest(hl2, length)
        let value: number[] = [];
        value.push(0)
        let fishValue: number[] = [];
        fishValue.push(0)
        for (let i = 1; i < hl2.length; i++) {
            //let newValu=this._round(this.add(this.multiplyConstant(this.subtractConstant(this.divide(this.subtract(hl2 ,low_),this.subtract(high_ , low_)),0.5) ,0.66) , this.multiplyConstant( this.shiftSeries(value,1,0),0.67)));
            let newValue = this._round(0.66 * ((hl2[i] - low_[i]) / (high_[i] - low_[i]) - 0.5) + 0.67 * value[i - 1])
            value.push(newValue)
        }
        for (let i = 1; i < value.length; i++) {
            let newfish = 0.5 * Math.log((1 + value[i]) / (1 - value[i])) + .5 * fishValue[i - 1]
            fishValue.push(newfish)
        }
        return fishValue;
    }


    subtract(series1: number[], series2: number[]) {
        const ans = [];
        const lengthDiff = series1.length - series2.length
        if (lengthDiff >= 0) {
            for (let i = 0; i < series2.length; i++) {
                ans.push(series1[i + lengthDiff] - series2[i]);
            }
        } else {
            for (let i = 0; i < series1.length; i++) {
                ans.push(series1[i] - series2[i - lengthDiff]);
            }
        }
        return ans;
    }

    multiplyConstant(series: number[], number: number): number[] {
        const ans: number[] = [];
        for (let i = 0; i < series.length; i++) {
            ans.push(series[i] * number);
        }
        return ans;
    }

    cum(values: any[]) {
        let result = 0;
        return values.map(value => result += value);
    }

    sign(values: any[]) {
        return values.map(value => (value > 0 ? 1 : value < 0 ? -1 : 0));
    }

    change(values: any[], length: number = 1) {
        const result = [];
        for (let index = length; index < values.length; index++) {
            const value = values[index];
            const prevValue = values[index - length];
            result.push(prevValue !== undefined ? value - prevValue : 0);
        }
        return result;
    }

    max(values: any[], number: number) {
        return values.map(value => Math.max(value, number));
    }

    compareMax(series1: number[], series2: number[]): number[] {
        const result: number[] = [];
        const maxSize = Math.max(series1.length, series2.length);
        for (let i = 0; i < maxSize; i++) {
            const value1 = series1[i] ?? Number.MIN_VALUE;
            const value2 = series2[i] ?? Number.MIN_VALUE;
            result.push(Math.max(value1, value2));
        }
        return result;
    }


    min(values: any[], number: number) {
        return values.map(value => Math.min(value, number));
    }

    compareMin(series1: number[], series2: number[]): number[] {
        const result: number[] = [];
        const maxSize = Math.max(series1.length, series2.length);
        for (let i = 0; i < maxSize; i++) {
            const value1 = series1[i] ?? Number.MAX_VALUE;
            const value2 = series2[i] ?? Number.MAX_VALUE;
            result.push(Math.min(value1, value2));
        }
        return result;
    }

    multiply(series1: number[], series2: number[]) {
        const ans = [];
        const lengthDiff = series1.length - series2.length
        if (lengthDiff >= 0) {
            for (let i = 0; i < series2.length; i++) {
                ans.push(series1[i + lengthDiff] * series2[i]);
            }
        } else {
            for (let i = 0; i < series1.length; i++) {
                ans.push(series1[i] * series2[i - lengthDiff]);
            }
        }
        return ans;
    }

    divide(series: number[], numbers: number[]): number[] {
        const ans = [];
        const lengthDiff = series.length - numbers.length;
        if (lengthDiff >= 0) {
            for (let i = 0; i < numbers.length; i++) {
                if (numbers[i] === 0) {
                    ans.push(0);
                } else {
                    ans.push(series[i + lengthDiff] / numbers[i]);
                }
            }
        } else {
            for (let i = 0; i < series.length; i++) {
                if (numbers[i] === 0) {
                    ans.push(0);
                } else {
                    ans.push(series[i] / numbers[i - lengthDiff]);
                }
            }
        }
        return ans;
    }

    alma(
        source: number[], period: number,
        offset: number, sigma: number,
        size: number = source.length
    ): number[] {
        return ta.alma(source, period, offset, sigma, source.length)
    }

    sma(src: number[], len: number): number[] {
        return ta.sma(src, len)
    }

    ema(src: number[], len: number): number[] {
        return ta.ema(src, len)
    }

    vwap(src: number[], vol: number[], timestamps: number[], calendarData?: CalendarData): number[] {
        return ta.vwap(src, vol, timestamps, calendarData)
    }

    expiryVwap(vwap: number[], volume: number[], timestamps: number[]): (number | null)[] {
        if (timestamps.length === 0) return [];

        const checkIfTimestampValidForExpiryVwap = (timestamp: number): boolean => {
            const date = new Date(timestamp - IST_OFFSET_MILLIS);
            const hour = date.getHours();
            const minutes = date.getMinutes();
            return hour === 15 && minutes >= 0 && minutes <= 30;
        };

        const output: (number | null)[] = [];
        let num = 0.0;
        let den = 0.0;

        let seenNonExpiryCandle = false
        for (let i = 0; i < vwap.length; i++) {
            if (seenNonExpiryCandle && checkIfTimestampValidForExpiryVwap(timestamps[i])) {
                num += vwap[i] * volume[i];
                den += volume[i];
                output.push(num / den);
            } else {
                seenNonExpiryCandle = true
                num = 0.0;
                den = 0.0;
                output.push(Number.NaN);
            }
        }
        return output;

    }

    vwma(arr: number[], vol: number[], interval: number): number[] {
        return ta.vwma(arr, vol, interval);
    }

    wma(arr: number[], interval: number): number[] {
        return ta.wma(arr, interval)
    }

    stdev(series: number[], interval: number): number[] {
        return ta.stddev(series, interval)
    }

    roc(series: number[], interval: number): number[] {
        return ta.roc(series, interval)
    }

    linreg(series: number[], interval: number): number[] {
        return ta.linreg(series, interval)
    }


    twap(source: number[], timestamps: number[], calendarData: CalendarData, anchor: string = "1D"): number[] {
        const output: number[] = [];

        if (!calendarData || !calendarData.dayToSessions || !(calendarData.dayToSessions instanceof Map)) {
            console.warn("TWAP: Invalid or missing calendarData; defaulting to NaN output.");
            return timestamps.map(() => NaN);
        }

        let currentPeriod = '';
        let priceSum = 0;
        let count = 0;

        for (let i = 0; i < timestamps.length; i++) {
            const time = timestamps[i];
            const date = new Date(time);

            let periodKey = '';
            if (anchor === "1D") {
                periodKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
            } else if (anchor === "1W") {

                const startOfYear = new Date(date.getFullYear(), 0, 1);
                const days = Math.floor((time - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
                const weekNum = Math.ceil((days + startOfYear.getDay() + 1) / 7);
                periodKey = `${date.getFullYear()}-W${weekNum}`;
            } else if (anchor === "1M") {
                periodKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM
            }

            if (periodKey !== currentPeriod) {
                currentPeriod = periodKey;
                priceSum = 0;
                count = 0;
            }

            const price = source[i];

            if (!isNaN(price)) {
                priceSum += price;
                count += 1;
            }

            output.push(count > 0 ? priceSum / count : NaN);
        }

        return output;
    }


    bop(): number[] {
        const close = this.candles.map((candle) => candle.close)
        const open = this.candles.map((candle) => candle.open)
        const high = this.candles.map((candle) => candle.high)
        const low = this.candles.map((candle) => candle.low)
        return this.divide(this.subtract(close, open), this.subtract(high, low));
    }

    lowest(values: number[], length: number): number[] {
        if (length > values.length) {
            throw new Error("Length exceeds the size of the array");
        }

        const output: number[] = [];

        for (let i = 0; i < values.length; i++) {
            let lowestValue = Number.MAX_VALUE;
            for (let j = Math.max(i - length + 1, 0); j <= i; j++) {
                if (values[j] < lowestValue) {
                    lowestValue = values[j];
                }
            }
            output.push(lowestValue);
        }

        return output;
    }


    highest(values: number[], length: number): number[] {
        if (length > values.length) {
            throw new Error("Length exceeds the size of the array");
        }

        const output: number[] = [];

        for (let i = 0; i < values.length; i++) {
            let highestValue = Number.MIN_VALUE;
            for (let j = Math.max(i - length + 1, 0); j <= i; j++) {
                if (values[j] > highestValue) {
                    highestValue = values[j];
                }
            }
            output.push(highestValue);
        }

        return output;
    }


    aroon(high: number[], low: number[], period: number): number[][] {
        return ta.aroon(high, low, period, high.length)
    }

    volume(): number[] {
        return this.candles.map((candle) => {
            return candle.customValues!["volume"] as number;
        });
    }

    log(series: number[]): number[] {
        const ans: number[] = [];
        for (const element of series) {
            ans.push(Math.log(element));
        }
        return ans;
    }

    log10(series: number[]): number[] {
        const ans: number[] = [];
        for (const element of series) {
            ans.push(Math.log10(element));
        }
        return ans;
    }

    atr(high: number[], low: number[], close: number[], period: number, size: number = high.length): number[] {
        return ta.atr(high, low, close, period, size)
    }

    hlc3(high: number[], low: number[], close: number[]): number[] {
        const hlc3Values: number[] = [];

        for (let i = 0; i < high.length; i++) {
            const hlc3 = (high[i] + low[i] + close[i]) / 3;
            hlc3Values.push(hlc3);
        }

        return hlc3Values;
    }

    aroonOsc(high: number[], low: number[], period: number): number[] {
        return ta.aroonosc(high, low, period, high.length)
    }

    sum(series: number[], period: number) {
        return ta.sum(series, period, series.length);
    }

    avg(series1: number[], series2: number[]): number[] {
        if (series1.length !== series2.length) {
            throw new Error("Series sizes must match");
        }

        const output: number[] = [];

        for (let i = 0; i < series1.length; i++) {
            const average = (series1[i] + series2[i]) / 2.0;
            output.push(average);
        }

        return output;
    }

    abs(values: number[]): number[] {
        return values.map(value => Math.abs(value));
    }

    addConstant(series: number[], constant: number): number[] {
        return series.map(value => value + constant);
    }

    subtractConstant(series: number[], constant: number): number[] {
        return series.map(value => value - constant);
    }

    williamsPercentR(length: number, src: number[]): number[] {
        const max = this.highest(src, length);
        const min = this.lowest(src, length);
        const ans: number[] = [];

        for (let i = 0; i < max.length; i++) {
            ans.push(100 * (src[i] - max[i]) / (max[i] - min[i]));
        }
        return ans;
    }

    shiftSeries(series: number[], shiftLength: number, defaultValue: number): number[] {
        const shiftedSeries: number[] = [];
        const size = series.length;
        for (let i = shiftLength; i < size; i++) {
            const newIndex = i - shiftLength;
            if (newIndex >= 0 && newIndex < size) {
                shiftedSeries.push(series[newIndex]);
            } else if (newIndex >= size && newIndex < size + shiftLength) {
                shiftedSeries.push(defaultValue)
            } else {
                shiftedSeries.push(NaN); // Or any other value to represent missing data
            }
        }
        return shiftedSeries;
    }

    stoch(high: number[], low: number[], close: number[], kperiod: number, kslow: number, dperiod: number, size: number) {
        return ta.stoch(high, low, close, kperiod, kslow, dperiod, high.length)
    }

    supertrend(high: number[], low: number[], close: number[], multiplier: number, period: number): [number[], number[]] {
        if (high.length !== low.length || low.length !== close.length) {
            throw new Error("Input lists must have the same size");
        }
        if (period <= 0 || multiplier <= 0) {
            throw new Error("Period and multiplier must be greater than zero");
        }

        const atrValues: number[] = ta.atr(high, low, close, period, high.length);
        /** `ta.atr` returns one value per bar from index (period - 1) onward: atrValues[k] aligns with bar i = k + (period - 1). */
        const atrAt = (barIndex: number): number => {
            const k = barIndex - (period - 1)
            if (k < 0 || k >= atrValues.length) {
                return NaN
            }
            return atrValues[k]
        }

        const upperBand: number[] = [];
        const lowerBand: number[] = [];
        const trendDirection: number[] = [];
        const superTrend: number[] = [];

        let prevLowerBand = 0.0;
        let prevUpperBand = 0.0;
        /** 1 = line follows upper band (bearish), -1 = line follows lower band (bullish) — matches prior flip logic. */
        let prevTrend = 1;

        for (let i = 0; i < close.length; i++) {
            const atr = atrAt(i)
            const hl2 = (high[i] + low[i]) / 2.0
            const upperBandValue = hl2 + multiplier * atr
            const lowerBandValue = hl2 - multiplier * atr

            if (i === 0) {
                prevLowerBand = Number.isFinite(lowerBandValue) ? lowerBandValue : 0
                prevUpperBand = Number.isFinite(upperBandValue) ? upperBandValue : 0
            }

            let updatedLowerBand: number
            let updatedUpperBand: number
            if (!Number.isFinite(atr)) {
                updatedLowerBand = prevLowerBand
                updatedUpperBand = prevUpperBand
            } else {
                updatedLowerBand =
                    lowerBandValue > prevLowerBand || (i > 0 && close[i - 1] < prevLowerBand)
                        ? lowerBandValue
                        : prevLowerBand
                updatedUpperBand =
                    upperBandValue < prevUpperBand || (i > 0 && close[i - 1] > prevUpperBand)
                        ? upperBandValue
                        : prevUpperBand
            }

            const prevAtr = atrAt(i - 1)
            let currDirection: number
            if (!Number.isFinite(atr)) {
                currDirection = prevTrend
            } else if (!Number.isFinite(prevAtr)) {
                // First bar with ATR: no prior ATR-based trend; infer from price vs bands (matches typical ST init).
                if (close[i] > updatedUpperBand) {
                    currDirection = -1
                } else if (close[i] < updatedLowerBand) {
                    currDirection = 1
                } else {
                    currDirection = prevTrend
                }
            } else if (prevTrend === 1) {
                currDirection = close[i] > updatedUpperBand ? -1 : 1
            } else {
                currDirection = close[i] < updatedLowerBand ? 1 : -1
            }

            const currSuperTrend = currDirection === -1 ? updatedLowerBand : updatedUpperBand
            const currLowerBand = currDirection === -1 ? updatedLowerBand : NaN
            const currUpperBand = currDirection === 1 ? updatedUpperBand : NaN

            prevLowerBand = updatedLowerBand
            prevUpperBand = updatedUpperBand
            prevTrend = currDirection

            lowerBand.push(currLowerBand)
            upperBand.push(currUpperBand)
            trendDirection.push(currDirection)
            superTrend.push(currSuperTrend)
        }

        return [upperBand, lowerBand];
    }


    smma(source: number[], length: number): number[] {
        if (length <= 1) {
            throw new Error("Length must be greater than 1");
        }
        if (source.length < length) {
            throw new Error("Input list size must be greater than or equal to length");
        }

        const smmaValues: number[] = [];
        let smma = 0;

        // Calculate the initial SMMA
        for (let i = 0; i < length; i++) {
            smma += source[i];
        }
        smma /= length;
        smmaValues.push(smma);

        // Calculate the rest of the SMMA values
        for (let i = length; i < source.length; i++) {
            smma = (smma * (length - 1) + source[i]) / length;
            smmaValues.push(smma);
        }

        return smmaValues;
    }

    numArray(value: number, length: number): number[] {
        const ans: number[] = []
        for (let i = 0; i < length; i++) {
            ans.push(value)
        }
        return ans
    }

    psar(high: number[], low: number[], accel_step: number, accel_max: number) {
        return ta.psar(high, low, accel_step, accel_max)
    }

    rma(values: number[], length: number, size: number = values.length): number[] {
        const rmaValues = new Array(size).fill(NaN)
        if (length < 1) return rmaValues

        const alpha = 1 / length

        let sum = 0
        let validCount = 0
        let prevRma: number | null = null

        for (let i = 0; i < size; i++) {
            const v = values[i]

            // skip invalid values
            if (!Number.isFinite(v)) {
                continue
            }

            // seed with SMA(length)
            if (prevRma === null) {
                sum += v
                validCount++

                if (validCount === length) {
                    prevRma = sum / length
                    rmaValues[i] = prevRma
                }
                continue
            }

            // Wilder smoothing
            prevRma = prevRma + alpha * (v - prevRma)
            rmaValues[i] = prevRma
        }

        return rmaValues;
    }

    tr(high: number[], low: number[], close: number[]) {
        return ta.tr(high, low, close)
    }

    renko(input: { candles: CandlestickData[], period?: number, atr?: boolean, brickSize?: number }): CandlestickData[] {
        let useATR = input.atr;
        let brickSize = input.brickSize || 0;
        const close = input.candles.map((candle) => candle.close)
        const open = input.candles.map((candle) => candle.open)
        const high = input.candles.map((candle) => candle.high)
        const low = input.candles.map((candle) => candle.low)
        if (useATR) {
            let atrResult = this.atr(high, low, close, input.period ?? 14);
            brickSize = atrResult[atrResult.length - 1];
        }
        const renkoBricks: CandlestickData[] = []
        let lastRenkoCandle = undefined
        for (let i = 0; i < input.candles.length; i++) {
            const presCandle = input.candles[i]
            if (lastRenkoCandle === undefined) {
                lastRenkoCandle = {
                    ...input.candles[i]
                }
                continue;
            }
            let absoluteMovementFromClose = Math.abs(presCandle.close - lastRenkoCandle.close);
            let absoluteMovementFromOpen = Math.abs(presCandle.close - lastRenkoCandle.open);

            if ((absoluteMovementFromClose >= brickSize) && (absoluteMovementFromOpen >= brickSize)) {
                let reference: number = absoluteMovementFromClose > absoluteMovementFromOpen ? lastRenkoCandle.open : lastRenkoCandle.close;
                let newClose = reference > presCandle.close ? (reference - brickSize) : (reference + brickSize)
                let renkoBrick: CandlestickData = {
                    ...presCandle,
                    open: reference,
                    // high: Math.max(lastRenkoCandle.high, presCandle.high),
                    // low: Math.min(lastRenkoCandle.low, presCandle.low),
                    high: Math.max(reference, newClose),
                    low: Math.min(reference, newClose),
                    close: reference > presCandle.close ? (reference - brickSize) : (reference + brickSize)
                };

                renkoBricks.push(renkoBrick);

                lastRenkoCandle = renkoBrick
            } else {
                lastRenkoCandle = {
                    ...presCandle,
                    high: Math.max(lastRenkoCandle.high, presCandle.high),
                    low: Math.min(lastRenkoCandle.low, presCandle.low),
                }
            }
        }
        return renkoBricks
    }

    chg(series: number[], interval: number): number[] {
        const ans = [];
        for (let i = 0; i < series.length - interval; i++) {
            ans.push(series[i + interval] - series[i]);
        }
        return ans;
    }

    chgpercent(series: number[], interval: number): number[] {
        const ans = [];
        for (let i = 0; i < series.length - interval; i++) {
            const change = series[i + interval] - series[i];
            const percent = series[i] !== 0 ? (change / series[i]) * 100 : 0;
            ans.push(percent)
        }
        return ans;
    }

    spread() {
        return this.candles.map((candle: any) => Number(((candle.topAsk - candle.topBid) / 100).toFixed(2)));
    }

    median(series: number[], interval: number): number[] {
        const ans = [];
        for (let i = 0; i <= series.length - interval; i++) {
            const window = series.slice(i, i + interval).sort((a, b) => a - b);
            let median;
            if (interval % 2 === 0) {
                const mid1 = window[interval / 2 - 1];
                const mid2 = window[interval / 2];
                median = (mid1 + mid2) / 2;
            } else {
                median = window[Math.floor(interval / 2)];
            }
            ans.push(median);
        }
        return ans;
    }

    logReturn(series: number[], interval: number) {
        const ans = [];
        for (let i = 0; i <= series.length - interval; i++) {
            const current = series[i + interval];
            const past = series[i];
            const logReturn = Math.log(current / past);
            ans.push(logReturn);
        }
        return ans;
    }

    percentileRank(series: number[], interval: number) {
        const ans = [];
        for (let i = 0; i <= series.length - interval; i++) {
            const window = series.slice(i, i + interval);
            const currentValue = window[window.length - 1];

            let count = 0;
            for (let j = 0; j < window.length; j++) {
                if (currentValue >= window[j]) count++;
            }

            const rank = (count / window.length) * 100;
            ans.push(rank);
        }
        return ans;
    }

    percentileNearestRank(source: number[], length: number, percentage: number): number[] {
        const size = source.length;
        const result: number[] = new Array(size).fill(NaN);

        if (length < 1 || percentage < 0 || percentage > 100) {
            return result;
        }

        for (let i = length - 1; i < size; i++) {
            const window = source.slice(i - length + 1, i + 1);
            const sorted = window.slice().sort((a, b) => a - b);
            const rank = Math.ceil((percentage / 100) * length);
            const idx = rank > 0 ? Math.min(rank - 1, sorted.length - 1) : 0;
            result[i] = sorted[idx];
        }

        return result;
    }

    day() {
        return this.candles.map(candle => {
            const istTimestamp = (candle.time as number) * 1000;
            const correctedUtcTimestamp = istTimestamp - (5.5 * 60 * 60 * 1000); // UTC
            const date = new Date(correctedUtcTimestamp);
            const yyyy = date.getFullYear();
            const mm = (date.getMonth() + 1).toString().padStart(2, '0');
            const dd = date.getDate().toString().padStart(2, '0');
            return parseFloat(`${yyyy}${mm}${dd}`);
        });
    }

    time() {
        return this.candles.map(candle => {
            const istTimestamp = (candle.time as number) * 1000;
            const correctedUtcTimestamp = istTimestamp - (5.5 * 60 * 60 * 1000); // UTC
            const date = new Date(correctedUtcTimestamp);
            const hh = date.getHours().toString().padStart(2, '0');
            const mm = date.getMinutes().toString().padStart(2, '0');
            return parseFloat(`${hh}${mm}`);
        });
    }

    dayOfWeek() {
        return this.candles.map(candle => {
            const istTimestamp = (candle.time as number) * 1000;
            const correctedUtcTimestamp = istTimestamp - (5.5 * 60 * 60 * 1000); // UTC
            const date = new Date(correctedUtcTimestamp);
            const jsDay = date.getDay();
            return jsDay === 0 ? 7 : jsDay;
        });
    }

    cmo(src: number[], length: number): number[] {
        if (src.length < length + 1) {
            throw new Error("Source array must be longer than length + 1");
        }
        if (length <= 0) {
            throw new Error("Length must be greater than 0");
        }

        const cmoValues: number[] = [];

        const momentum: number[] = [];
        for (let i = 1; i < src.length; i++) {
            momentum.push(src[i] - src[i - 1]);
        }

        // Helper functions to separate positive and negative momentum
        const getPositiveMomentum = (m: number): number => m >= 0.0 ? m : 0.0;
        const getNegativeMomentum = (m: number): number => m >= 0.0 ? 0.0 : -m;

        // Calculate CMO for each period
        for (let i = length - 1; i < momentum.length; i++) {
            let sumPositive = 0;
            let sumNegative = 0;

            // Sum positive and negative momentum over the length period
            for (let j = i - length + 1; j <= i; j++) {
                sumPositive += getPositiveMomentum(momentum[j]);
                sumNegative += getNegativeMomentum(momentum[j]);
            }

            // Calculate CMO: 100 * (sum_positive - sum_negative) / (sum_positive + sum_negative)
            const totalSum = sumPositive + sumNegative;
            const cmoValue = totalSum !== 0 ? 100 * (sumPositive - sumNegative) / totalSum : 0;

            cmoValues.push(cmoValue);
        }

        return cmoValues;
    }

    rci(src: number[], length: number): number[] {
        if (src.length < length) {
            throw new Error("Source array must be at least as long as the length parameter");
        }
        if (length <= 1) {
            throw new Error("Length must be greater than 1");
        }

        const rciValues: number[] = [];
        for (let i = length - 1; i < src.length; i++) {
            const window = src.slice(i - length + 1, i + 1);
            const n = window.length;

            const timeRanks = Array.from({ length: n }, (_, index) => index + 1);
            const indexed = window.map((value, index) => ({ value, index }));
            indexed.sort((a, b) => a.value - b.value);

            const priceRanks = new Array(n);
            let rankIndex = 0;
            while (rankIndex < n) {
                let tieEnd = rankIndex;
                while (tieEnd < n && indexed[tieEnd].value === indexed[rankIndex].value) {
                    tieEnd++;
                }

                const avgRank = (rankIndex + tieEnd + 1) / 2;
                for (let k = rankIndex; k < tieEnd; k++) {
                    priceRanks[indexed[k].index] = avgRank;
                }

                rankIndex = tieEnd;
            }

            let sumD2 = 0;
            for (let j = 0; j < n; j++) {
                const d = timeRanks[j] - priceRanks[j];
                sumD2 += d * d;
            }

            const rho = n <= 1 ? 0 : 1 - (6 * sumD2) / (n * (n * n - 1));

            rciValues.push(rho * 100);
        }

        return rciValues;
    }

    hullMa(src: number[], length: number): number[] {
        const halfLength = Math.floor(length / 2)
        const sqrtLength = Math.floor(Math.sqrt(length))
        const wma1 = this.wma(src, length)
        const wma2 = this.wma(src, halfLength)
        const diff = this.subtract(this.multiplyConstant(wma2, 2), wma1)
        return this.wma(diff, sqrtLength)
    }

    dpo(src: number[], length: number, isCentered: boolean): number[] {
        const barsback = (length >> 1) + 1;

        const ma = this.sma(src, length);
        const out: number[] = new Array(src.length).fill(NaN);

        for (let i = 0; i < src.length; i++) {
            if (isCentered) {
                if (i >= length - 1 && i - barsback >= 0 && !Number.isNaN(ma[i])) {
                    out[i] = src[i - barsback] - ma[i];
                }
            } else {
                if (i - barsback >= length - 1 && !Number.isNaN(ma[i - barsback])) {
                    out[i] = src[i] - ma[i - barsback];
                }
            }
        }
        return out;
    }


    mcginley(src: number[], length: number): number[] {
        const mg: number[] = [];

        for (let i = 0; i < src.length; i++) {
            if (i === 0) {
                const slice = src.slice(0, i + 1);
                const emaInit = slice.reduce((a, b) => a + b, 0) / slice.length;
                mg.push(emaInit);
            } else {
                const prevMg = mg[i - 1];
                mg.push(prevMg + (src[i] - prevMg) / (length * Math.pow(src[i] / prevMg, 4)));
            }
        }

        return mg;
    }

    mfi(high: number[], low: number[], close: number[], volume: number[], length: number): number[] {
        return ta.mfi(high, low, close, volume, length)
    }

    connorRsi(source: number[], lenRsi: number, lenUpDown: number, lenRoc: number): number[] {
        const minBarsRequired = Math.max(lenRsi, lenUpDown) + lenRoc + 1
        if (source.length < minBarsRequired) return []

        const updown = (series: number[]): number[] => {
            const result: number[] = [0]
            let streak = 0

            for (let i = 1; i < series.length; i++) {
                const curr = series[i]
                const prev = series[i - 1]

                if (curr === prev) streak = 0
                else if (curr > prev) streak = streak <= 0 ? 1 : streak + 1
                else streak = streak >= 0 ? -1 : streak - 1

                result.push(streak)
            }
            return result
        }

        const rsiValues = this.rsi(source, lenRsi)
        const upDownRsi = this.rsi(updown(source), lenUpDown)
        const percentRank = this.percentileRank(this.roc(source, 1), lenRoc)

        const startIndex = Math.max(lenRsi, lenUpDown, lenRoc)
        const crsi: number[] = []

        for (let i = startIndex; i < source.length; i++) {
            const rsiIdx = i - lenRsi
            const upDownRsiIdx = i - lenUpDown
            const percentIdx = i - lenRoc

            const rsi = rsiValues[rsiIdx]
            const upRsi = upDownRsi[upDownRsiIdx]
            const pr = percentRank[percentIdx]

            if ([rsi, upRsi, pr].some((v) => isNaN(v))) {
                crsi.push(NaN)
                continue
            }

            crsi.push((rsi + upRsi + pr) / 3)
        }

        return crsi
    }

    tsi(source: number[], longLength: number, shortLength: number): number[] {
        const doubleSmooth = (src: number[], long: number, short: number): number[] => {
            const firstSmooth = this.ema(src, long)
            return this.ema(firstSmooth, short)
        }

        const pc = this.change(source)
        const absPc = pc.map(Math.abs)

        const doubleSmoothedPc = doubleSmooth(pc, longLength, shortLength)
        const doubleSmoothedAbsPc = doubleSmooth(absPc, longLength, shortLength)

        return this.multiplyConstant(this.divide(doubleSmoothedPc, doubleSmoothedAbsPc), 100)

    }

    dev(src: number[], len: number): number[] {
        const result: number[] = new Array(src.length).fill(NaN);
        const smaSeries = this.sma(src, len);

        for (let i = 0; i < src.length; i++) {
            if (i < len - 1) continue;

            const mean = smaSeries[i];
            if (isNaN(mean)) continue;

            let sumAbs = 0;
            let count = 0;

            for (let j = i - len + 1; j <= i; j++) {
                const v = src[j];
                if (!isNaN(v)) {
                    sumAbs += Math.abs(v - mean);
                    count++;
                }
            }

            result[i] = count > 0 ? sumAbs / count : NaN;
        }

        return result;
    }

    adx(high: number[], low: number[], close: number[], diLength: number, adxLength: number, size: number = high.length): number[] {
        const { plusDI, minusDI } = this.calculateDIComponents(high, low, close, diLength, size)

        const dx: number[] = []
        for (let i = 0; i < plusDI.length; i++) {
            const sum = plusDI[i] + minusDI[i]
            dx.push(sum === 0 ? 0 : (100 * Math.abs(plusDI[i] - minusDI[i])) / sum)
        }

        return this.rma(dx, adxLength)
    }

    dmi(high: number[], low: number[], close: number[], diLength: number, adxLength: number, size: number = high.length): { adx: number[]; plusDI: number[]; minusDI: number[] } {
        const { plusDI, minusDI } = this.calculateDIComponents(high, low, close, diLength, size)
        const adx = this.adx(high, low, close, diLength, adxLength, size)

        return { adx, plusDI, minusDI }
    }

    chopZone(src: number[]): [number[], number[]] {
        if (src.length < 34) return [[],[]]
        const periods = 30
        const pi = Math.atan(1) * 4
        const high = this.candles.map((candle) => candle.high)
        const low = this.candles.map((candle) => candle.low)
        const close = this.candles.map((candle) => candle.close)
        const avg = this.hlc3(high, low, close)
        const ema34 = this.ema(src, 34)
        const highestHigh = this.highest(high, periods)
        const lowestLow = this.lowest(low, periods)
        const arr = Array(src.length).fill(1);
        const emaAngles: number[] = []
        for (let i = 1; i < ema34.length; i++) {
            const span = (25 / (highestHigh[i] - lowestLow[i])) * lowestLow[i]
            const x1_ema34 = 0
            const x2_ema34 = 1
            const y1_ema34 = 0
            const y2_ema34 = ((ema34[i - 1] - ema34[i]) / avg[i]) * span
            const c_ema34 = Math.sqrt((x2_ema34 - x1_ema34) * (x2_ema34 - x1_ema34) + (y2_ema34 - y1_ema34) * (y2_ema34 - y1_ema34))
            const emaAngle_1 = Math.round((180 * Math.acos((x2_ema34 - x1_ema34) / c_ema34)) / pi)
            const emaAngle = y2_ema34 > 0 ? -emaAngle_1 : emaAngle_1
            emaAngles.push(emaAngle)
        }
        return [emaAngles,arr]
    }

    swma(source: number[]): number[] {
        const result: number[] = new Array(source.length).fill(NaN);

        for (let i = 3; i < source.length; i++) {
            const v0 = source[i - 3];
            const v1 = source[i - 2];
            const v2 = source[i - 1];
            const v3 = source[i];

            if (!isNaN(v0) && !isNaN(v1) && !isNaN(v2) && !isNaN(v3)) {
                result[i] = (v0 + 2 * v1 + 2 * v2 + v3) / 6;
            }
        }

        return result;
    }


    sar(high: number[], low: number[], start: number = 0.02, inc: number = 0.02, max: number = 0.2): number[] {
        const size = high.length
        if (size < 2) return []

        const result: number[] = new Array(size).fill(NaN)

        let isLong = high[1] + low[1] > high[0] + low[0] // trend direction
        let af = start
        let ep = isLong ? high[1] : low[1]               // extreme point
        let sar = isLong ? low[0] : high[0]              // initial SAR

        for (let i = 1; i < size; i++) {
            sar = sar + af * (ep - sar)

            if (isLong) {
                sar = Math.min(sar, low[i - 1], low[Math.max(i - 2, 0)])

                if (high[i] > ep) {
                    ep = high[i]
                    af = Math.min(af + inc, max)
                }

                if (low[i] < sar) {
                    isLong = false
                    sar = ep
                    ep = low[i]
                    af = start
                }
            } else {
                sar = Math.max(sar, high[i - 1], high[Math.max(i - 2, 0)])

                if (low[i] < ep) {
                    ep = low[i]
                    af = Math.min(af + inc, max)
                }

                if (high[i] > sar) {
                    isLong = true
                    sar = ep
                    ep = high[i]
                    af = start
                }
            }

            if (isLong) sar = Math.min(sar, low[i])
            else sar = Math.max(sar, high[i])

            result[i] = sar
        }

        return result
    }

    advanceDeclineRatioBars(close: number[], open: number[], length: number): number[] {
        if (close.length !== open.length) {
            throw new Error("close and open arrays must have the same length");
        }

        const result: number[] = [];
        const isUp = close.map((c, i) => (c - open[i]) >= 0 ? 1 : 0);

        for (let i = 0; i < close.length; i++) {
            if (i + 1 < length) {
                result.push(NaN); // insufficient data
                continue;
            }

            let upBars = 0;
            let downBars = 0;

            for (let j = i - length + 1; j <= i; j++) {
                if (isUp[j] === 1) upBars++;
                else downBars++;
            }

            const ad = downBars === 0 ? upBars : upBars / downBars;
            result.push(ad);
        }

        return result;
    }

    crossSeries(short: number[], long: number[]): number[] {
        const len = Math.min(short.length, long.length);
        const result: number[] = new Array(len).fill(NaN);

        for (let i = 1; i < len; i++) {
            const prevDiff = short[i - 1] - long[i - 1];
            const currDiff = short[i] - long[i];

            const crossed =
                (prevDiff < 0 && currDiff >= 0) || (prevDiff > 0 && currDiff <= 0);

            result[i - 1] = crossed ? short[i - 1] : NaN;
        }

        return result;
    }

    rvi(src: number[], length: number = 10, len: number = 14): number[] {
        if (src.length < Math.max(length, len) + 1) {
            return []
        }

        const stddev = this.stdev(src, length)

        const changes: number[] = []
        for (let i = 1; i < src.length; i++) {
            changes.push(src[i] - src[i - 1])
        }

        const alignedStddev: number[] = []
        for (let i = 0; i < changes.length; i++) {
            const stddevIndex = i + 1
            if (stddevIndex < stddev.length) {
                alignedStddev.push(stddev[stddevIndex])
            } else {
                alignedStddev.push(stddev[stddev.length - 1] || 0)
            }
        }

        const upperSeries: number[] = []
        const lowerSeries: number[] = []

        for (let i = 0; i < changes.length; i++) {
            const stddevValue = alignedStddev[i]

            upperSeries.push(changes[i] <= 0 ? 0 : stddevValue)
            lowerSeries.push(changes[i] > 0 ? 0 : stddevValue)
        }

        const upperEma = this.ema(upperSeries, len)
        const lowerEma = this.ema(lowerSeries, len)

        const rvi: number[] = []
        const minLength = Math.min(upperEma.length, lowerEma.length)

        for (let i = 0; i < minLength; i++) {
            const upper = upperEma[i]
            const lower = lowerEma[i]
            const sum = upper + lower

            if (sum !== 0) {
                rvi.push((upper / sum) * 100)
            } else {
                rvi.push(50) // Default to middle value when sum is 0
            }
        }

        return rvi
    }

    private calculateDIComponents(high: number[], low: number[], close: number[], len: number, size: number = high.length): { plusDI: number[]; minusDI: number[] } {
        const plusDM: number[] = []
        const minusDM: number[] = []
        const trueRange: number[] = []

        // Initial values
        plusDM.push(0)
        minusDM.push(0)
        trueRange.push(high[0] - low[0])

        for (let i = 1; i < size; i++) {
            const upMove = high[i] - high[i - 1]
            const downMove = low[i - 1] - low[i]

            plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0)
            minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0)

            const hl = high[i] - low[i]
            const hc = Math.abs(high[i] - close[i - 1])
            const lc = Math.abs(low[i] - close[i - 1])
            trueRange.push(Math.max(hl, hc, lc))
        }

        // Apply RMA smoothing
        const smoothedTR = this.rma(trueRange, len)
        const smoothedPlusDM = this.rma(plusDM, len)
        const smoothedMinusDM = this.rma(minusDM, len)

        // Calculate +DI and -DI
        const plusDI: number[] = []
        const minusDI: number[] = []

        for (let i = 0; i < smoothedTR.length; i++) {
            if (smoothedTR[i] === 0) {
                plusDI.push(0)
                minusDI.push(0)
            } else {
                plusDI.push((100 * smoothedPlusDM[i]) / smoothedTR[i])
                minusDI.push((100 * smoothedMinusDM[i]) / smoothedTR[i])
            }
        }

        return { plusDI, minusDI }
    }

    async fetchCurrentOpenInterests(): Promise<any> {
        if (!this.security) {
            return Promise.reject("Security not set")
        }
        const response = await getCurrentOpenInterests({
            security: {
                exchange: this.security.exchange,
                code: this.security.code
            }, timeBucket: { timeSeries: TimeBucket_TimeSeries.DAY, value: 1 }
        })
        return { expiryOpenInterests: response.expiryOpenInterests }
    }

}

export function sma(data: { series: number[], interval: number }): number[] {
    const { series, interval } = data;

    return ta.sma(series, interval)
}

export function stdev(data: { series: number[], length: number }): number[] {
    const { series, length } = data

    return ta.stddev(series, length)
}

export function deviation(arr: number[]): number {


    let n = arr.length;
    let sum = 0;

    arr.map(function (data) {
        sum += data;
    });

    let mean = sum / n;

    let variance = 0.0;
    let v1 = 0.0;
    let v2 = 0.0;
    let stddev = 0.0

    if (n !== 1) {
        for (let i = 0; i < n; i++) {
            v1 = v1 + (arr[i] - mean) * (arr[i] - mean);
            v2 = v2 + (arr[i] - mean);
        }

        v2 = v2 * v2 / n;
        variance = (v1 - v2) / (n - 1);
        if (variance < 0) {
            variance = 0;
        }
        stddev = Math.sqrt(variance);
    }

    return Math.round(stddev * 100) / 100
}

// export function bbands(candles: CandlestickData[], interval: number, base: string){
//     let smaData : number[] = [] 
//     switch(base){
//         case "close":
//             smaData = sma(candles.map((candle)=>(candle as CandlestickData).close), interval)
//             break;
//     }
//     console.log("inside bbbands")
//     const basis: SingleValueData[] = []
//     const upper: SingleValueData[] = []
//     const lower: SingleValueData[] = []
//     for(let i=interval;i<candles.length;i++){
//         basis.push({
//             time: candles[i].time,
//             value: smaData[i-interval]
//         })
//         const deviation = stdev(candles.slice(i-interval, i).map((candle)=> (candle as CandlestickData).close))
//         upper.push({
//             time: candles[i].time,
//             value: smaData[i-interval]+deviation
//         })
//         lower.push({
//             time: candles[i].time,
//             value: smaData[i-interval]-deviation
//         })
//     }
//     return [upper, basis, lower]
// }


